import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Table,
  Form,
  Input,
  Button,
  Space,
  Tabs,
  Typography,
  Modal,
  InputNumber,
  message,
  Tag,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Link, useSearchParams } from 'react-router-dom';
import { SearchOutlined, PlusOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/services/api.service';
import dayjs from 'dayjs';
import { MtcDocumentLink } from '@/components/documents/MtcDocumentLink';

const { Title, Text } = Typography;

type RowKind = 'CONSUMPTION' | 'OUTPUT';

interface MatrixRow {
  id: string;
  row_kind: RowKind;
  heat_number: string;
  dimension?: string;
  batch_number: string | null;
  work_order_id: string | null;
  work_order_number: string | null;
  raw_item_code: string | null;
  raw_item_name: string | null;
  finished_item_code: string | null;
  finished_item_name: string | null;
  finished_serial_or_tag: string | null;
  quantity: number;
  mtc_document_id: string | null;
  mtc_document_number?: string | null;
  recorded_at: string;
  material_issuance_item_id: string | null;
}

interface MatrixSearchResponse {
  rows: MatrixRow[];
  total: number;
  page: number;
  page_size: number;
}

async function fetchMatrix(params: Record<string, string | number | undefined>) {
  const res = await api.get<{ data: MatrixSearchResponse }>('/inventory/traceability/matrix', { params });
  const body = res.data;
  if (!body?.data) throw new Error('Empty traceability matrix response');
  return body.data;
}

async function fetchForward(heat_number: string, dimension?: string) {
  const params: Record<string, string> = { heat_number };
  if (dimension?.trim()) params.dimension = dimension.trim();
  const res = await api.get<{
    data: { heat_number: string; consumptions: MatrixRow[]; outputs: MatrixRow[] };
  }>('/inventory/traceability/forward', { params });
  const body = res.data;
  if (!body?.data) throw new Error('Empty forward trace response');
  return body.data;
}

async function fetchBackward(serial_or_tag: string) {
  const res = await api.get<{
    data: { serial_or_tag: string; outputs: MatrixRow[]; consumptions: MatrixRow[] };
  }>('/inventory/traceability/backward', { params: { serial_or_tag } });
  const body = res.data;
  if (!body?.data) throw new Error('Empty backward trace response');
  return body.data;
}

const TraceabilityMatrixPage: React.FC = () => {
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const [matrixForm] = Form.useForm();
  const [forwardHeat, setForwardHeat] = useState('');
  const [forwardDimension, setForwardDimension] = useState('');
  const [forwardTicket, setForwardTicket] = useState(0);
  const [backwardSerial, setBackwardSerial] = useState('');
  const [backwardTicket, setBackwardTicket] = useState(0);
  const [matrixParams, setMatrixParams] = useState<Record<string, string>>({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [outputOpen, setOutputOpen] = useState(false);
  const [outputForm] = Form.useForm();

  // Auto-run search from URL: heat_number, serial_or_tag, cpr= (also legacy heat/serial)
  useEffect(() => {
    const heat =
      searchParams.get('heat_number')?.trim() || searchParams.get('heat')?.trim() || '';
    const serial =
      searchParams.get('serial_or_tag')?.trim() || searchParams.get('serial')?.trim() || '';
    const cpr =
      searchParams.get('cpr')?.trim() ||
      searchParams.get('customer_property_receipt_id')?.trim() ||
      '';
    if (!heat && !serial && !cpr) return;

    const next: Record<string, string> = {};
    if (heat) next.heat_number = heat;
    if (serial) next.serial_or_tag = serial;
    if (cpr) next.cpr = cpr;
    matrixForm.setFieldsValue({
      heat_number: heat || undefined,
      serial_or_tag: serial || undefined,
      cpr: cpr || undefined,
    });
    setPage(1);
    setMatrixParams(next);

    if (heat && !serial) {
      setForwardHeat(heat);
      setForwardTicket((t) => t + 1);
    }
    if (serial) {
      setBackwardSerial(serial);
      setBackwardTicket((t) => t + 1);
    }
  }, [searchParams, matrixForm]);

  const matrixQuery = useQuery({
    queryKey: ['traceability-matrix', matrixParams, page, pageSize],
    queryFn: () =>
      fetchMatrix({
        ...matrixParams,
        page,
        page_size: pageSize,
      }),
    enabled: Object.keys(matrixParams).length > 0,
  });

  const forwardQuery = useQuery({
    queryKey: ['traceability-forward', forwardHeat, forwardDimension, forwardTicket],
    queryFn: () => fetchForward(forwardHeat.trim(), forwardDimension),
    enabled: forwardTicket > 0 && Boolean(forwardHeat.trim()),
  });

  const backwardQuery = useQuery({
    queryKey: ['traceability-backward', backwardSerial, backwardTicket],
    queryFn: () => fetchBackward(backwardSerial.trim()),
    enabled: backwardTicket > 0 && Boolean(backwardSerial.trim()),
  });

  const outputMutation = useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const res = await api.post('/inventory/traceability/production-output', body);
      return res.data;
    },
    onSuccess: () => {
      message.success('Production output trace recorded');
      setOutputOpen(false);
      outputForm.resetFields();
      qc.invalidateQueries({ queryKey: ['traceability-matrix'] });
      qc.invalidateQueries({ queryKey: ['traceability-forward'] });
      qc.invalidateQueries({ queryKey: ['traceability-backward'] });
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(err?.response?.data?.message || 'Failed to record output');
    },
  });

  const columns: ColumnsType<MatrixRow> = useMemo(
    () => [
      {
        title: 'Kind',
        dataIndex: 'row_kind',
        width: 120,
        render: (k: RowKind) =>
          k === 'CONSUMPTION' ? <Tag color="blue">Consumption</Tag> : <Tag color="green">Output</Tag>,
      },
      { title: 'Heat', dataIndex: 'heat_number', width: 120, render: (v: string) => v || '—' },
      {
        title: 'Dimension',
        dataIndex: 'dimension',
        width: 120,
        render: (v: string | undefined) => v || '—',
      },
      { title: 'Lot/Batch', dataIndex: 'batch_number', width: 110, render: (v: string | null) => v || '—' },
      {
        title: 'Work order',
        key: 'wo',
        width: 160,
        render: (_: unknown, r: MatrixRow) =>
          r.work_order_id ? (
            <Link to={`/mfg/work-orders/${r.work_order_id}`}>
              {r.work_order_number || r.work_order_id}
            </Link>
          ) : (
            '—'
          ),
      },
      {
        title: 'Raw item',
        key: 'raw',
        ellipsis: true,
        render: (_: unknown, r: MatrixRow) =>
          r.raw_item_code ? `${r.raw_item_code}${r.raw_item_name ? ` — ${r.raw_item_name}` : ''}` : '—',
      },
      {
        title: 'Finished item',
        key: 'fin',
        ellipsis: true,
        render: (_: unknown, r: MatrixRow) =>
          r.finished_item_code
            ? `${r.finished_item_code}${r.finished_item_name ? ` — ${r.finished_item_name}` : ''}`
            : '—',
      },
      { title: 'Serial/Tag', dataIndex: 'finished_serial_or_tag', width: 130, render: (v: string | null) => v || '—' },
      {
        title: 'Qty',
        dataIndex: 'quantity',
        width: 90,
        render: (q: number) => (Number.isFinite(Number(q)) ? Number(q).toLocaleString() : '—'),
      },
      {
        title: 'MTC document',
        key: 'mtc_doc',
        width: 280,
        render: (_: unknown, r: MatrixRow) =>
          r.mtc_document_id ? (
            <MtcDocumentLink
              documentId={r.mtc_document_id}
              documentNumber={r.mtc_document_number ?? null}
              compact
            />
          ) : (
            '—'
          ),
      },
      {
        title: 'Recorded',
        dataIndex: 'recorded_at',
        width: 160,
        render: (d: string) => (d ? dayjs(d).format('YYYY-MM-DD HH:mm') : '—'),
      },
    ],
    []
  );

  const onMatrixSearch = () => {
    const v = matrixForm.getFieldsValue();
    const next: Record<string, string> = {};
    if (v.heat_number?.trim()) next.heat_number = v.heat_number.trim();
    if (v.dimension?.trim()) next.dimension = v.dimension.trim();
    if (v.work_order_id?.trim()) next.work_order_id = v.work_order_id.trim();
    if (v.raw_item_id?.trim()) next.raw_item_id = v.raw_item_id.trim();
    if (v.finished_item_id?.trim()) next.finished_item_id = v.finished_item_id.trim();
    if (v.serial_or_tag?.trim()) next.serial_or_tag = v.serial_or_tag.trim();
    if (v.cpr?.trim()) next.cpr = v.cpr.trim();
    if (v.row_kind) next.row_kind = v.row_kind;
    if (Object.keys(next).length === 0) {
      message.warning(
        'Enter at least one filter (heat, dimension, work order, item, serial, EOP receipt, or row kind)'
      );
      return;
    }
    setPage(1);
    setMatrixParams(next);
  };

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Title level={3} style={{ marginBottom: 4 }}>
            Traceability matrix
          </Title>
          <Text type="secondary">
            Heat-linked material issues to work orders are captured automatically. Completing a job on{' '}
            <strong>Job scheduling</strong> with produced quantity posts <strong>finished goods</strong> to the work
            order&apos;s target warehouse and records the traceability output when a stockable finished item can be
            resolved. Use the button below for ad-hoc trace rows without using job completion.
          </Text>
        </div>

        <Card size="small">
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setOutputOpen(true)}>
            Record production output
          </Button>
        </Card>

        <Tabs
          items={[
            {
              key: 'matrix',
              label: 'Matrix search',
              children: (
                <Card>
                  <Form form={matrixForm} layout="inline" onFinish={onMatrixSearch} style={{ marginBottom: 16 }}>
                    <Form.Item name="heat_number">
                      <Input placeholder="Heat number" allowClear style={{ width: 140 }} />
                    </Form.Item>
                    <Form.Item name="dimension">
                      <Input placeholder="Dimension" allowClear style={{ width: 140 }} />
                    </Form.Item>
                    <Form.Item name="work_order_id">
                      <Input placeholder="Work order ID" allowClear style={{ width: 200 }} />
                    </Form.Item>
                    <Form.Item name="raw_item_id">
                      <Input placeholder="Raw item ID" allowClear style={{ width: 200 }} />
                    </Form.Item>
                    <Form.Item name="finished_item_id">
                      <Input placeholder="Finished item ID" allowClear style={{ width: 200 }} />
                    </Form.Item>
                    <Form.Item name="serial_or_tag">
                      <Input placeholder="Serial / tag" allowClear style={{ width: 160 }} />
                    </Form.Item>
                    <Form.Item name="cpr">
                      <Input placeholder="EOP # or id" allowClear style={{ width: 160 }} />
                    </Form.Item>
                    <Form.Item name="row_kind">
                      <Input placeholder="Row kind (CONSUMPTION|OUTPUT)" allowClear style={{ width: 220 }} />
                    </Form.Item>
                    <Form.Item>
                      <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                        Search
                      </Button>
                    </Form.Item>
                  </Form>
                  <Table<MatrixRow>
                    rowKey="id"
                    loading={matrixQuery.isFetching}
                    columns={columns}
                    dataSource={matrixQuery.data?.rows ?? []}
                    pagination={{
                      current: page,
                      pageSize,
                      total: matrixQuery.data?.total ?? 0,
                      showSizeChanger: true,
                      onChange: (p, ps) => {
                        setPage(p);
                        setPageSize(ps || 25);
                      },
                    }}
                    scroll={{ x: 1200 }}
                  />
                </Card>
              ),
            },
            {
              key: 'forward',
              label: 'Forward (heat → WO → output)',
              children: (
                <Card>
                  <Space style={{ marginBottom: 16 }}>
                    <Input
                      placeholder="Heat number"
                      value={forwardHeat}
                      onChange={(e) => setForwardHeat(e.target.value)}
                      style={{ width: 200 }}
                    />
                    <Input
                      placeholder="Dimension (optional)"
                      value={forwardDimension}
                      onChange={(e) => setForwardDimension(e.target.value)}
                      style={{ width: 200 }}
                    />
                    <Button
                      type="primary"
                      onClick={() => {
                        if (!forwardHeat.trim()) {
                          message.warning('Enter a heat number');
                          return;
                        }
                        setForwardTicket((t) => t + 1);
                      }}
                    >
                      Load
                    </Button>
                  </Space>
                  <Title level={5}>Consumption</Title>
                  <Table<MatrixRow>
                    rowKey="id"
                    loading={forwardQuery.isFetching}
                    columns={columns}
                    dataSource={forwardQuery.data?.consumptions ?? []}
                    pagination={false}
                    size="small"
                  />
                  <Title level={5} style={{ marginTop: 24 }}>
                    Outputs (same work orders)
                  </Title>
                  <Table<MatrixRow>
                    rowKey="id"
                    loading={forwardQuery.isFetching}
                    columns={columns}
                    dataSource={forwardQuery.data?.outputs ?? []}
                    pagination={false}
                    size="small"
                  />
                </Card>
              ),
            },
            {
              key: 'backward',
              label: 'Backward (serial → heat)',
              children: (
                <Card>
                  <Space style={{ marginBottom: 16 }}>
                    <Input
                      placeholder="Serial or tag"
                      value={backwardSerial}
                      onChange={(e) => setBackwardSerial(e.target.value)}
                      style={{ width: 220 }}
                    />
                    <Button
                      type="primary"
                      onClick={() => {
                        if (!backwardSerial.trim()) {
                          message.warning('Enter serial or tag');
                          return;
                        }
                        setBackwardTicket((t) => t + 1);
                      }}
                    >
                      Load
                    </Button>
                  </Space>
                  <Title level={5}>Matching outputs</Title>
                  <Table<MatrixRow>
                    rowKey="id"
                    loading={backwardQuery.isFetching}
                    columns={columns}
                    dataSource={backwardQuery.data?.outputs ?? []}
                    pagination={false}
                    size="small"
                  />
                  <Title level={5} style={{ marginTop: 24 }}>
                    Consumptions (same work orders)
                  </Title>
                  <Table<MatrixRow>
                    rowKey="id"
                    loading={backwardQuery.isFetching}
                    columns={columns}
                    dataSource={backwardQuery.data?.consumptions ?? []}
                    pagination={false}
                    size="small"
                  />
                </Card>
              ),
            },
          ]}
        />
      </Space>

      <Modal
        title="Record production output (trace)"
        open={outputOpen}
        onCancel={() => setOutputOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Form
          form={outputForm}
          layout="vertical"
          onFinish={(vals) => outputMutation.mutate(vals)}
        >
          <Form.Item name="work_order_id" label="Work order ID" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="finished_item_id" label="Finished item ID" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="quantity" label="Quantity" rules={[{ required: true }]}>
            <InputNumber min={0.0001} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="finished_serial_or_tag" label="Serial / tag (optional)">
            <Input />
          </Form.Item>
          <Form.Item name="work_order_line_item_id" label="WO line item ID (optional)">
            <Input />
          </Form.Item>
          <Form.Item name="source_reference" label="Source reference (optional)">
            <Input />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={outputMutation.isPending} block>
            Save
          </Button>
        </Form>
      </Modal>
    </div>
  );
};

export default TraceabilityMatrixPage;
